// Subscription management component for premium users
import { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../../auth/AuthContext';
import { StripeService } from '../../services/stripeService';
import { supabase } from '../../config/supabase';

interface SubscriptionInfo {
  isActive: boolean;
  status: string;
  currentPeriodEnd?: Date;
  cancelAtPeriodEnd?: boolean;
}

export function SubscriptionManager() {
  const { user } = useAuth();
  const [subscription, setSubscription] = useState<SubscriptionInfo | null>(null);
  const [loading, setLoading] = useState(true);

  const loadSubscription = useCallback(async () => {
    if (!user) return;
    
    try {
      const status = await StripeService.getSubscriptionStatus(user.uid);
      setSubscription(status);
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error('Error loading subscription:', error);
    } finally {
      setLoading(false);
    }
  }, [user]);

  useEffect(() => {
    if (user) {
      loadSubscription();
    }
  }, [user, loadSubscription]);

  const handleManageSubscription = async () => {
    if (!user) return;

    try {
      // Get user's Stripe customer ID from profile
      const { data: profile, error: profileError } = await supabase
        .from('profiles')
        .select('stripe_customer_id')
        .eq('id', user.uid)
        .single();

      if (profileError) {
        alert('Error fetching user profile. Please try again.');
        return;
      }

      if (profile?.stripe_customer_id) {
        const portalUrl = await StripeService.createPortalSession(profile.stripe_customer_id);
        window.location.href = portalUrl;
      } else {
        alert('No subscription found. Please upgrade to premium first.');
      }
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error('Error opening customer portal:', error);
      alert('Failed to open subscription management. Please try again.');
    }
  };

  if (!user || user.tier !== 'premium') {
    return null;
  }

  if (loading) {
    return (
      <div className="subscription-manager">
        <div className="loading">Loading subscription info...</div>
      </div>
    );
  }

  return (
    <div className="subscription-manager">
      <div className="subscription-info">
        <div className="status-badge">
          <span className={`status ${subscription?.isActive ? 'active' : 'inactive'}`}>
            {subscription?.isActive ? '✓ Active' : '⚠ Inactive'}
          </span>
        </div>

        {subscription?.currentPeriodEnd && (
          <div className="period-info">
            <p>
              <strong>Next billing date:</strong> {' '}
              {subscription.currentPeriodEnd.toLocaleDateString()}
            </p>
          </div>
        )}

        {/* Cancellation Status Alert */}
        {subscription?.status === 'canceled' && subscription?.currentPeriodEnd && (
          <div className="cancellation-alert">
            <h4>⚠️ Subscription Canceled</h4>
            <p>
              Your premium access will end on{' '}
              <strong>{subscription.currentPeriodEnd.toLocaleDateString()}</strong>
            </p>
            <p>You can reactivate anytime before this date to continue premium access.</p>
          </div>
        )}

        {subscription?.cancelAtPeriodEnd && subscription?.currentPeriodEnd && (
          <div className="cancellation-alert">
            <h4>⚠️ Subscription Will Cancel</h4>
            <p>
              Your subscription is set to cancel on{' '}
              <strong>{subscription.currentPeriodEnd.toLocaleDateString()}</strong>
            </p>
            <p>You can reactivate it anytime before this date.</p>
          </div>
        )}

        <div className="features-list">
          <h4>Your Premium Features:</h4>
          <ul>
            <li>Unlimited guitar arrangements</li>
            <li>All scales and modes (15+ scales)</li>
            <li>Unlimited chord progressions</li>
            <li>Unlimited cloud presets with sync</li>
            <li>Premium audio types (Electric, Classical, Bass)</li>
            <li>Advanced CAGED visualizations</li>
            <li>Scale overlay system</li>
            <li>Export capabilities</li>
          </ul>
        </div>
      </div>

      <div className="subscription-actions">
        <button 
          className="manage-btn"
          onClick={handleManageSubscription}
        >
          {subscription?.status === 'canceled' || subscription?.cancelAtPeriodEnd ? 
            'Reactivate Subscription' : 'Manage Subscription'}
        </button>
        <p className="help-text">
          {subscription?.status === 'canceled' || subscription?.cancelAtPeriodEnd ?
            'Reactivate your subscription to continue premium access' :
            'Update payment method, download invoices, or cancel your subscription'}
        </p>
      </div>
    </div>
  );
}
